import * as React from "react";

import { AnsweredQuestionWithId } from "@teamprevent/common/model";
import { submitInvalidQuestionnaire as submitInvalidQuestionnaireAction } from "@teamprevent/common/questionnaires/actions";
import { FileAnswer } from "@teamprevent/common/questionnaires/model";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { Loader } from "semantic-ui-react";

import {
    deleteConsultationEvidence,
    loadHealthSurveillanceRepeatMedicalConsultationForClinicFromServer,
    saveHealthSurveillanceRepeatMedicalConsultationToServer,
    submitHealthSurveillanceRepeatMedicalConsultationToServer
} from "../actions";
import { ApplicationState } from "../../applicationState";

import { getHealthSurveillanceConsultationEvidenceApi, getOhCaseDocumentDownloadLink } from "../healthSurveillanceApi";

import { useClinicAndAppointment } from "../../calendar/clinics/hooks";

import { useMedicalTypesForAppointment } from "../hooks";

import { useCustomMedicalTypes } from "../../customMedicalTypes/hooks/useCustomMedicalTypes";

import {
    HealthSurveillanceRepeatMedicalConsultationKey,
} from "./HealthSurveillanceConsultation";

import { HealthSurveillanceConsultation } from "./HealthSurveillanceConsultation";

import { push } from "redux-little-router";

export const ClinicHealthSurveillanceRepeatConsultationForAppointment: React.FunctionComponent = () => {
    const dispatch = useDispatch();

    const date = useSelector((state: ApplicationState) => state.router.params!.date);
    const clinicId = useSelector((state: ApplicationState) => state.router.params!.clinicId!);
    const appointmentId = useSelector((state: ApplicationState) => state.router.params!.appointmentId!);
    const taskId = useSelector((state: ApplicationState) => state.router.params!.taskId);

    const { appointment, isLoaded: clinicLoaded, clinic } = useClinicAndAppointment({ clinicId, appointmentId });

    const navigateToNextPage = React.useCallback(() => dispatch(push(`/clinics/calendar/${date}/${clinicId}`)), [clinicId, date, dispatch]);

    const submitQuestionnaire = React.useCallback(async (isHavsTierThreeOrFour: boolean): Promise<boolean> => {
        await dispatch(submitHealthSurveillanceRepeatMedicalConsultationToServer(
            clinicId,
            appointmentId,
            taskId,
            HealthSurveillanceRepeatMedicalConsultationKey,
            isHavsTierThreeOrFour));
        return true;
    }, [appointmentId, clinicId, dispatch, taskId]);

    const submitInvalidQuestionnaire = React.useCallback(() =>
        dispatch(submitInvalidQuestionnaireAction(HealthSurveillanceRepeatMedicalConsultationKey)), [dispatch]);

    const saveQuestionnaire = React.useCallback((questions: AnsweredQuestionWithId[], eTag: string | null) =>
        dispatch(saveHealthSurveillanceRepeatMedicalConsultationToServer(clinicId, appointmentId, taskId, questions, eTag)),
    [appointmentId, clinicId, dispatch, taskId]);

    const onDeleteEvidence = React.useCallback(
        (_fileId: string, _fileName: string, ohCaseDocumentId: string) => {
            return dispatch(deleteConsultationEvidence(
                appointment!.ohCaseId!, appointment!.customer!.id, ohCaseDocumentId
            ));
        },
        [appointment, dispatch]);

    const getQuestionnaire = React.useCallback((abort: AbortController)  => {
        return dispatch(loadHealthSurveillanceRepeatMedicalConsultationForClinicFromServer(clinicId, appointmentId, taskId, abort));
    }, [appointmentId, clinicId, dispatch, taskId]);

    const fileDownload = React.useCallback((fileAnswer: FileAnswer): string => {
        return getOhCaseDocumentDownloadLink(appointment!.ohCaseId!, fileAnswer);
    }, [appointment]);

    const {
        medicalTypes: medicalTypesForConsultation,
        refetch: loadMedicalTypesForConsultation
    } = useMedicalTypesForAppointment({ appointmentId, clinicId }, { enable: false });

    const {
        customMedicalTypes,
        refetch: loadCustomMedicalTypes
    } = useCustomMedicalTypes({ enable: false });

    if (clinicLoaded === false) {
        return <Loader active />;
    }
    const customerId = clinic.customer!.id;
    const fileUploadApi = getHealthSurveillanceConsultationEvidenceApi(clinicId, appointmentId, customerId);

    return (<HealthSurveillanceConsultation
        fileUploadApi={fileUploadApi}
        questionnaireKey={HealthSurveillanceRepeatMedicalConsultationKey}
        onDeleteFile={onDeleteEvidence}
        getDownloadLink={fileDownload}
        getQuestionnaire={getQuestionnaire}
        medicalTypesForConsultation={medicalTypesForConsultation}
        loadMedicalTypesForConsultation={loadMedicalTypesForConsultation}
        customMedicalTypes={customMedicalTypes}
        loadCustomMedicalTypes={loadCustomMedicalTypes}
        task={undefined}
        submitQuestionnaire={submitQuestionnaire}
        navigateToNextPage={navigateToNextPage}
        submitInvalidQuestionnaire={submitInvalidQuestionnaire}
        saveQuestionnaire={saveQuestionnaire}
        readonly={appointment.isClinicalNotesComplete}
        customerId={customerId}
        ohCaseId={appointment!.ohCaseId!}
        appointmentId={appointmentId}
    />);
};
